
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function PremiumDealershipApplication() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    businessType: '',
    experience: '',
    location: '',
    targetMarket: '',
    investmentTimeline: '',
    fundingSource: '',
    businessPlan: '',
    marketingExperience: '',
    teamSize: '',
    currentRevenue: '',
    expansionPlans: '',
    competitorAnalysis: '',
    marketingBudget: '',
    operationalExperience: '',
    territoryPreference: '',
    scalingStrategy: '',
    partnershipExperience: '',
    technologyExperience: '',
    sustainabilityFocus: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d439c9pha0trr1ha9i90', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          businessName: '',
          businessType: '',
          experience: '',
          location: '',
          targetMarket: '',
          investmentTimeline: '',
          fundingSource: '',
          businessPlan: '',
          marketingExperience: '',
          teamSize: '',
          currentRevenue: '',
          expansionPlans: '',
          competitorAnalysis: '',
          marketingBudget: '',
          operationalExperience: '',
          territoryPreference: '',
          scalingStrategy: '',
          partnershipExperience: '',
          technologyExperience: '',
          sustainabilityFocus: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/dealership" className="inline-flex items-center text-purple-100 hover:text-white mb-6">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Dealership
          </Link>
          <div className="flex items-center mb-4">
            <h1 className="text-4xl font-bold">Premium Dealership Application</h1>
            <span className="ml-4 bg-gold-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-semibold">Elite Package</span>
          </div>
          <p className="text-xl text-purple-100">Investment: $200,000 | For established business leaders</p>
        </div>
      </div>

      {/* Application Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Premium Package Details</h2>
            <div className="bg-purple-50 p-6 rounded-lg">
              <h3 className="font-semibold text-purple-800 mb-2">What's Included:</h3>
              <ul className="text-purple-700 space-y-1">
                <li>• 6 Geo Domes + 4 Safari Tents + 2 Tiny Homes</li>
                <li>• Enterprise POS & Booking System</li>
                <li>• Premium Marketing & Advertising Package</li>
                <li>• 6-Week Comprehensive Training</li>
                <li>• Territory Rights (200-mile radius)</li>
                <li>• Complete Solar Power Systems</li>
                <li>• Glamping Pods & Luxury Amenities</li>
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form id="premium-dealership-form">
            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Business Type *
                  </label>
                  <select
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Business Type</option>
                    <option value="hospitality-chain">Hospitality Chain</option>
                    <option value="real-estate-development">Real Estate Development</option>
                    <option value="tourism-enterprise">Tourism Enterprise</option>
                    <option value="franchise-owner">Multi-Franchise Owner</option>
                    <option value="investment-group">Investment Group</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Business Experience *
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Experience</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10-15">10-15 years</option>
                    <option value="15-20">15-20 years</option>
                    <option value="20+">20+ years</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="currentRevenue" className="block text-sm font-medium text-gray-700 mb-2">
                    Current Annual Revenue *
                  </label>
                  <select
                    id="currentRevenue"
                    name="currentRevenue"
                    value={formData.currentRevenue}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Revenue Range</option>
                    <option value="500k-1m">$500,000 - $1,000,000</option>
                    <option value="1m-2m">$1,000,000 - $2,000,000</option>
                    <option value="2m-5m">$2,000,000 - $5,000,000</option>
                    <option value="5m+">$5,000,000+</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Territory Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    placeholder="City, State/Province, Country"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label htmlFor="territoryPreference" className="block text-sm font-medium text-gray-700 mb-2">
                    Territory Preference *
                  </label>
                  <select
                    id="territoryPreference"
                    name="territoryPreference"
                    value={formData.territoryPreference}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Preference</option>
                    <option value="single-territory">Single Large Territory</option>
                    <option value="multiple-territories">Multiple Territories</option>
                    <option value="regional-exclusive">Regional Exclusive Rights</option>
                    <option value="national-expansion">National Expansion Rights</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="partnershipExperience" className="block text-sm font-medium text-gray-700 mb-2">
                    Partnership/Franchise Experience *
                  </label>
                  <select
                    id="partnershipExperience"
                    name="partnershipExperience"
                    value={formData.partnershipExperience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Experience</option>
                    <option value="none">No Previous Experience</option>
                    <option value="single">Single Partnership/Franchise</option>
                    <option value="multiple">Multiple Partnerships/Franchises</option>
                    <option value="master">Master Franchise/Regional Partner</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Investment Details */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Investment & Operations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="investmentTimeline" className="block text-sm font-medium text-gray-700 mb-2">
                    Investment Timeline *
                  </label>
                  <select
                    id="investmentTimeline"
                    name="investmentTimeline"
                    value={formData.investmentTimeline}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Timeline</option>
                    <option value="immediate">Immediate (1-30 days)</option>
                    <option value="1-3-months">1-3 months</option>
                    <option value="3-6-months">3-6 months</option>
                    <option value="6-12-months">6-12 months</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="fundingSource" className="block text-sm font-medium text-gray-700 mb-2">
                    Funding Source *
                  </label>
                  <select
                    id="fundingSource"
                    name="fundingSource"
                    value={formData.fundingSource}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Funding Source</option>
                    <option value="corporate-funds">Corporate Funds</option>
                    <option value="business-loan">Business Loan</option>
                    <option value="investor-group">Investor Group</option>
                    <option value="private-equity">Private Equity</option>
                    <option value="combination">Combination</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="marketingBudget" className="block text-sm font-medium text-gray-700 mb-2">
                    Annual Marketing Budget *
                  </label>
                  <select
                    id="marketingBudget"
                    name="marketingBudget"
                    value={formData.marketingBudget}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Budget Range</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="500k+">$500,000+</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="operationalExperience" className="block text-sm font-medium text-gray-700 mb-2">
                    Large-Scale Operations Experience *
                  </label>
                  <select
                    id="operationalExperience"
                    name="operationalExperience"
                    value={formData.operationalExperience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Experience Level</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="expert">Expert</option>
                    <option value="enterprise">Enterprise Level</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="technologyExperience" className="block text-sm font-medium text-gray-700 mb-2">
                    Technology Integration Experience *
                  </label>
                  <select
                    id="technologyExperience"
                    name="technologyExperience"
                    value={formData.technologyExperience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Experience Level</option>
                    <option value="basic">Basic</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Team Size *
                  </label>
                  <select
                    id="teamSize"
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Team Size</option>
                    <option value="10-15">10-15 people</option>
                    <option value="15-25">15-25 people</option>
                    <option value="25-50">25-50 people</option>
                    <option value="50+">50+ people</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Strategic Planning */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Strategic Planning & Vision</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="targetMarket" className="block text-sm font-medium text-gray-700 mb-2">
                    Comprehensive Market Analysis *
                  </label>
                  <textarea
                    id="targetMarket"
                    name="targetMarket"
                    value={formData.targetMarket}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Provide detailed analysis of your target market, customer segments, and market opportunity..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.targetMarket.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="businessPlan" className="block text-sm font-medium text-gray-700 mb-2">
                    Strategic Business Plan *
                  </label>
                  <textarea
                    id="businessPlan"
                    name="businessPlan"
                    value={formData.businessPlan}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your comprehensive business plan, strategic objectives, and 5-year vision..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.businessPlan.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="scalingStrategy" className="block text-sm font-medium text-gray-700 mb-2">
                    Scaling & Growth Strategy *
                  </label>
                  <textarea
                    id="scalingStrategy"
                    name="scalingStrategy"
                    value={formData.scalingStrategy}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Detail your strategy for scaling the business and achieving rapid growth..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.scalingStrategy.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="expansionPlans" className="block text-sm font-medium text-gray-700 mb-2">
                    Multi-Territory Expansion Plans *
                  </label>
                  <textarea
                    id="expansionPlans"
                    name="expansionPlans"
                    value={formData.expansionPlans}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your plans for expanding across multiple territories or regions..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.expansionPlans.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="competitorAnalysis" className="block text-sm font-medium text-gray-700 mb-2">
                    Competitive Advantage Strategy *
                  </label>
                  <textarea
                    id="competitorAnalysis"
                    name="competitorAnalysis"
                    value={formData.competitorAnalysis}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    placeholder="Analyze your competition and describe your unique competitive advantages..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.competitorAnalysis.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="sustainabilityFocus" className="block text-sm font-medium text-gray-700 mb-2">
                    Sustainability & Innovation Focus *
                  </label>
                  <textarea
                    id="sustainabilityFocus"
                    name="sustainabilityFocus"
                    value={formData.sustainabilityFocus}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your commitment to sustainability and innovation in glamping..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.sustainabilityFocus.length}/500 characters</p>
                </div>
                <div>
                  <label htmlFor="marketingExperience" className="block text-sm font-medium text-gray-700 mb-2">
                    Marketing & Brand Management Experience *
                  </label>
                  <select
                    id="marketingExperience"
                    name="marketingExperience"
                    value={formData.marketingExperience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 pr-8"
                  >
                    <option value="">Select Experience Level</option>
                    <option value="advanced">Advanced</option>
                    <option value="professional">Professional</option>
                    <option value="expert">Expert</option>
                    <option value="enterprise">Enterprise Level</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Strategic Information
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Any additional strategic information, partnerships, or unique qualifications..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting Application...' : 'Submit Premium Dealership Application'}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 mr-2"></i>
                  <p className="text-green-800">Application submitted successfully! We'll contact you within 24 hours.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 mr-2"></i>
                  <p className="text-red-800">There was an error submitting your application. Please try again.</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
